import { useMemo } from "react";
import { Joyride, EVENTS, ACTIONS, STATUS, type Step, type TooltipRenderProps, type EventHandler } from "react-joyride";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useOnboarding } from "./OnboardingProvider";
import { findTourById } from "./tours";

/**
 * Custom tooltip component so the dialog matches the shadcn theme used across
 * the rest of the studio. Joyride passes us render props for every button —
 * we wire them into our own <Button> components.
 */
function ShadcnTooltip({
  backProps,
  closeProps,
  index,
  primaryProps,
  skipProps,
  step,
  tooltipProps,
  isLastStep,
  size,
}: TooltipRenderProps) {
  return (
    <div
      {...tooltipProps}
      className="relative w-[320px] max-w-[90vw] rounded-xl border border-border bg-popover text-popover-foreground shadow-lg overflow-hidden"
    >
      {/* Close (X) */}
      <button
        {...closeProps}
        className={cn(
          "absolute top-2 right-2 inline-flex h-7 w-7 items-center justify-center rounded-md",
          "text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
        )}
        aria-label="Close tour"
      >
        <X className="w-3.5 h-3.5" />
      </button>

      <div className="px-4 pt-4 pb-3">
        {step.title && (
          <h3 className="font-display font-semibold text-sm pr-8 mb-1">
            {step.title}
          </h3>
        )}
        <div className="text-sm text-muted-foreground leading-relaxed">
          {step.content}
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 px-4 py-3 border-t border-border bg-card/50">
        <span className="text-xs text-muted-foreground tabular-nums">
          {index + 1} of {size}
        </span>
        <div className="flex items-center gap-1.5">
          {index > 0 && (
            <Button
              {...(backProps as any)}
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
            >
              Back
            </Button>
          )}
          {!isLastStep && (
            <Button
              {...(skipProps as any)}
              variant="ghost"
              size="sm"
              className="h-7 text-xs text-muted-foreground"
            >
              Skip
            </Button>
          )}
          <Button
            {...(primaryProps as any)}
            size="sm"
            className="h-7 text-xs gradient-primary text-primary-foreground"
          >
            {isLastStep ? "Got it" : "Next"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function OnboardingTour() {
  const { currentTour, completeTour, dismissTour } = useOnboarding();

  const tour = currentTour ? findTourById(currentTour) : null;

  const joyrideSteps: Step[] = useMemo(() => {
    if (!tour) return [];
    return tour.steps
      .filter((s) => (s.showIf ? s.showIf() : true))
      .map((s) => ({
        target: s.target,
        title: s.title,
        content: s.body,
        placement: s.placement ?? "auto",
        disableBeacon: s.disableBeacon ?? true,
        spotlightPadding: 6,
      }));
  }, [tour]);

  const handleEvent: EventHandler = (data) => {
    const { action, status, type } = data;
    if (!tour) return;

    // Completed the whole tour
    if (status === STATUS.FINISHED) {
      completeTour(tour.id);
      return;
    }
    // Skipped or closed
    if (status === STATUS.SKIPPED) {
      dismissTour(tour.id);
      return;
    }
    if (type === EVENTS.TOUR_END && action === ACTIONS.CLOSE) {
      dismissTour(tour.id);
      return;
    }
    // Target not found — dismiss so we don't block the UI indefinitely.
    if (type === EVENTS.TARGET_NOT_FOUND) {
      console.warn(`[onboarding] target not found for tour "${tour.id}" at step`, data.step);
      dismissTour(tour.id);
    }
  };

  if (!tour || joyrideSteps.length === 0) return null;

  return (
    <Joyride
      steps={joyrideSteps}
      run={!!currentTour}
      continuous
      showSkipButton
      disableOverlayClose
      scrollToFirstStep
      tooltipComponent={ShadcnTooltip}
      onEvent={handleEvent}
      options={{
        // Match shadcn theme: dim overlay + subtle primary accent for spotlight.
        overlayColor: "rgba(0, 0, 0, 0.55)",
        arrowColor: "hsl(var(--popover))",
        backgroundColor: "hsl(var(--popover))",
        textColor: "hsl(var(--popover-foreground))",
        primaryColor: "hsl(var(--primary))",
        zIndex: 10000,
        spotlightRadius: 8,
      }}
    />
  );
}
